import { useState, useEffect, useRef, useCallback } from 'react';
import { generateShortSlug } from '../../utils/helpers';
import { listAppointments, upsertAppointment } from '../../api/appointments';
import { listMeetings, upsertMeeting } from '../../api/meetings/meetings';

const convertToValidDate = (dateValue) => {
  try {
    if (!dateValue) return null;

    if (dateValue instanceof Date) {
      return Number.isNaN(dateValue.getTime()) ? null : dateValue;
    }

    if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
      const date = dateValue.toDate();
      return Number.isNaN(date.getTime()) ? null : date;
    }

    if (dateValue?.seconds !== undefined) {
      const date = new Date(dateValue.seconds * 1000);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    if (typeof dateValue === 'number') {
      const date = new Date(dateValue > 99999999999 ? dateValue : dateValue * 1000);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    if (typeof dateValue === 'string') {
      const date = new Date(dateValue);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    return null;
  } catch {
    return null;
  }
};

const archiveCheckIntervalMs = () => {
  const now = new Date();
  if (now.getHours() === 23 && now.getMinutes() >= 55) return 60000;
  if (now.getHours() >= 8 && now.getHours() < 20) return 60000;
  return 5 * 60000;
};

export const useAppointmentAutoArchiver = (companyId) => {
  const [lastCheck, setLastCheck] = useState(null);
  const isRunningRef = useRef(false);

  const isEndOfDay = () => {
    const now = new Date();
    return now.getHours() === 23 && now.getMinutes() >= 55;
  };

  const isPastAppointment = (appointmentDate) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    today.setHours(0, 0, 0, 0);

    const appointmentDay = new Date(appointmentDate);
    appointmentDay.setHours(0, 0, 0, 0);

    return appointmentDay < today;
  };

  const checkAndArchiveAppointments = useCallback(async () => {
    if (isRunningRef.current || !companyId) {
      return;
    }

    try {
      isRunningRef.current = true;
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const result = await listAppointments({ companyId, archived: false });
      const appointments = result.appointments || [];

      for (const appointmentData of appointments) {
        const appointmentDateTime = convertToValidDate(appointmentData.dateTime);
        if (!appointmentDateTime) {
          continue;
        }

        const appointmentEnd = new Date(appointmentDateTime);
        appointmentEnd.setMinutes(appointmentEnd.getMinutes() + (appointmentData.duration || 60));

        const appointmentDate = new Date(
          appointmentDateTime.getFullYear(),
          appointmentDateTime.getMonth(),
          appointmentDateTime.getDate()
        );

        const normalizedStatus = String(appointmentData.status || '').toLowerCase();
        const shouldArchive =
          normalizedStatus === 'cancelled' ||
          normalizedStatus === 'canceled' ||
          (normalizedStatus === 'completed' && isPastAppointment(appointmentDateTime)) ||
          (
            (
              normalizedStatus === 'no show' ||
              (
                (normalizedStatus === 'pending' || normalizedStatus === 'rescheduled') &&
                appointmentEnd < now
              )
            ) &&
            appointmentDate < today &&
            isEndOfDay()
          );

        if (!shouldArchive) {
          continue;
        }

        try {
          const archiveStatus = normalizedStatus === 'pending' ? 'completed' : appointmentData.status;
          await upsertAppointment({
            companyId,
            id: appointmentData.id,
            appointment: {
              shortSlug: appointmentData.shortSlug || generateShortSlug(),
              status: archiveStatus,
              archived: true,
              archivedAt: now.toISOString(),
              originalAppointmentId: appointmentData.id,
              storageLocation: 'company',
            },
          });
        } catch (archiveError) {
          console.error(
            '[AutoArchiver] Failed to archive appointment:',
            appointmentData.id,
            archiveError
          );
        }
      }

      setLastCheck(now);
    } catch (error) {
      console.error('[AutoArchiver] checkAndArchiveAppointments failed:', error);
    } finally {
      isRunningRef.current = false;
    }
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return undefined;

    let timer;
    const runCheck = () => {
      checkAndArchiveAppointments();
      timer = setTimeout(runCheck, archiveCheckIntervalMs());
    };

    checkAndArchiveAppointments();
    timer = setTimeout(runCheck, archiveCheckIntervalMs());

    return () => clearTimeout(timer);
  }, [companyId, checkAndArchiveAppointments]);

  return {
    lastCheck,
    isRunning: isRunningRef.current,
    forceCheck: checkAndArchiveAppointments,
  };
};

/**
 * Marks past-day active meetings archived so lists and tab counts stay accurate.
 */
export const useMeetingAutoArchiver = (companyId) => {
  const [lastMeetingCheck, setLastMeetingCheck] = useState(null);
  const isRunningRef = useRef(false);

  const checkAndArchiveMeetings = useCallback(async () => {
    if (isRunningRef.current || !companyId) {
      return;
    }

    try {
      isRunningRef.current = true;
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      startOfToday.setHours(0, 0, 0, 0);

      const meetings = await listMeetings({ companyId, archived: false });

      for (const meetingData of meetings) {
        const status = (meetingData.status || 'pending').toLowerCase();
        if (status === 'cancelled' || status === 'canceled') {
          continue;
        }
        if (meetingData.archived || meetingData.archivedAt) {
          continue;
        }

        const start = convertToValidDate(meetingData.dateTime);
        if (!start) {
          continue;
        }

        const meetingEnd = new Date(start);
        meetingEnd.setMinutes(meetingEnd.getMinutes() + (meetingData.duration || 60));

        const shouldArchive =
          status === 'pending' ||
          status === 'confirmed' ||
          status === 'completed';

        if (!shouldArchive || meetingEnd >= now || start >= startOfToday) {
          continue;
        }

        try {
          const nextStatus =
            status === 'pending' || status === 'confirmed'
              ? 'completed'
              : meetingData.status || 'completed';

          await upsertMeeting({
            companyId,
            id: meetingData.id,
            meeting: {
              shortSlug: meetingData.shortSlug || generateShortSlug(),
              status: nextStatus,
              archived: true,
              archivedAt: now.toISOString(),
              originalMeetingId: meetingData.id,
              autoArchived: true,
            },
          });
        } catch {
          // continue other meetings
        }
      }

      setLastMeetingCheck(now);
    } catch {
      // ignore
    } finally {
      isRunningRef.current = false;
    }
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return undefined;

    let timer;
    const runCheck = () => {
      checkAndArchiveMeetings();
      timer = setTimeout(runCheck, archiveCheckIntervalMs());
    };

    checkAndArchiveMeetings();
    timer = setTimeout(runCheck, archiveCheckIntervalMs());

    return () => clearTimeout(timer);
  }, [companyId, checkAndArchiveMeetings]);

  return {
    lastMeetingCheck,
    isMeetingArchiverRunning: isRunningRef.current,
    forceMeetingArchiveCheck: checkAndArchiveMeetings,
  };
};
